import { forwardRef } from 'react'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import { getFormattedValueParts } from '../utils/riskProfile.utils'
import { mapRiskProfileResponseToSingleElementSummary } from '../chart/RiskProfileChartData'
import { CATEGORY_COLOURS, FALLBACK_COLOUR } from '../constants/riskProfile.constants'
import type { RiskProfileResponse, RiskProfileMetricOption } from '../types/riskProfile.types'

export interface RiskByElementSummaryProps {
  data: RiskProfileResponse
  metricOption: RiskProfileMetricOption
}

export const RiskByElementSummary = forwardRef<HTMLDivElement, RiskByElementSummaryProps>(
  ({ data, metricOption }, ref) => {
    const currencySymbol = useAtomValue(currencySymbolAtom)
    const isDamageSeverity = metricOption.metric === 'damage_severity'
    const isPercentage = metricOption.type === 'percentage'

    const defaultUnit =
      metricOption.metric === 'damage_cost' || metricOption.metric === 'replacement_cost'
        ? currencySymbol
        : null

    const summary = mapRiskProfileResponseToSingleElementSummary(data, metricOption)

    if (!summary) {
      return <p className="text-sm text-muted-foreground">No comparable data for this selection.</p>
    }

    const unit = summary.unit ?? defaultUnit
    const formattedTotalValueAndPercentValue = getFormattedValueParts(
      summary.totalValue,
      metricOption,
      unit,
    )
    const formattedValue = getFormattedValueParts(summary.value, metricOption, unit)

    return (
      <div ref={ref} className="flex h-full min-h-[350px] flex-col">
        <div className="flex flex-wrap items-baseline">
          <span className="text-5xl font-semibold leading-none tracking-tight text-foreground">
            {isPercentage ? formattedTotalValueAndPercentValue.value : formattedValue.value}
          </span>
          {formattedValue.unit && (
            <span className="ml-2 text-base font-normal text-foreground">
              {isPercentage ? formattedTotalValueAndPercentValue.unit : formattedValue.unit}
            </span>
          )}
          <span className="ml-1 text-base font-normal text-foreground">at risk</span>
        </div>

        {!isPercentage && !isDamageSeverity && (
          <div className="flex flex-wrap items-baseline mt-2">
            <span className="ml-2 text-base font-normal text-foreground">
              of {formattedTotalValueAndPercentValue.value}
            </span>
            {formattedValue.unit && (
              <span className="ml-1 text-base font-normal text-foreground">
                {isPercentage ? formattedTotalValueAndPercentValue.unit : formattedValue.unit}
              </span>
            )}
            <span className="ml-1 text-base font-normal text-foreground">total</span>
          </div>
        )}

        <div className="mt-8 flex h-10 w-full overflow-hidden rounded-md bg-muted">
          {summary.categories.map((category) => {
            if (category.percentOfTotal <= 0) return null
            return (
              <div
                key={category.key}
                style={{
                  width: `${category.percentOfTotal}%`,
                  backgroundColor: CATEGORY_COLOURS[category.label] ?? FALLBACK_COLOUR,
                }}
              />
            )
          })}
        </div>

        <dl className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(80px,1fr))] gap-x-4 gap-y-8">
          {summary.categories
            .filter((category) => category.percentOfTotal > 0)
            .map((category) => {
              const colour = CATEGORY_COLOURS[category.label] ?? FALLBACK_COLOUR
              const formattedCategoryValue = getFormattedValueParts(
                category.value,
                metricOption,
                unit,
              )

              let mainRowInfo: string = formattedCategoryValue.value
              if (isPercentage) {
                mainRowInfo = category.percentOfTotal.toFixed(2) + '%'
              }

              return (
                <div key={category.key} className="min-w-0">
                  <dt className="h-1 w-full rounded-full" style={{ backgroundColor: colour }} />
                  <dt className="mt-2 text-sm text-muted-foreground">{category.label}</dt>
                  <dd className="mt-2 flex flex-wrap items-baseline gap-x-1 gap-y-1">
                    <span className="min-w-0 text-2xl font-semibold leading-none tracking-tight text-foreground">
                      {mainRowInfo}
                    </span>
                    {formattedCategoryValue.unit && (
                      <span className="text-sm font-normal text-foreground">
                        {formattedCategoryValue.unit}
                      </span>
                    )}
                  </dd>
                  {!isPercentage && (
                    <dd className="mt-2 text-sm text-muted-foreground">
                      {category.percentOfTotal.toLocaleString('en-NZ', {
                        maximumFractionDigits: isDamageSeverity ? 2 : 0,
                      })}
                      % of network
                    </dd>
                  )}
                </div>
              )
            })}
        </dl>
      </div>
    )
  },
)

RiskByElementSummary.displayName = 'RiskByElementSummary'
